import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";

const faqs = [
  {
    question: "¿Qué es la Inteligencia Artificial aplicada a la SST?",
    answerText:
      "Es el uso de tecnologías inteligentes para prevenir accidentes, optimizar reportes, identificar riesgos en tiempo real y mejorar la toma de decisiones en Seguridad y Salud en el Trabajo.",
    answer: (
      <>
        Es el uso de tecnologías inteligentes para prevenir accidentes, optimizar reportes,
        identificar riesgos en tiempo real y mejorar la toma de decisiones en{" "}
        <a href="/blog/inteligencia-artificial-en-sst" className="text-[#FFD100] font-bold hover:underline">
          Seguridad y Salud en el Trabajo
        </a>.
      </>
    ),
  },
  {
    question: "¿Cómo se aplica la IA en la salud ocupacional?",
    answerText:
      "Permite detectar enfermedades laborales, analizar patrones de ausentismo, hacer diagnósticos preventivos y optimizar planes de bienestar con datos reales.",
    answer: (
      <>
        La IA permite detectar{" "}
        <a href="/blog/enfermedades-laborales-con-ia" className="text-[#FFD100] font-bold hover:underline">
          enfermedades laborales
        </a>
        , analizar patrones de ausentismo, hacer diagnósticos preventivos y optimizar planes de bienestar con datos reales.
      </>
    ),
  },
  {
    question: "¿Qué herramientas de IA se usan en seguridad laboral?",
    answerText:
      "Algoritmos predictivos, visión por computadora, asistentes virtuales y detección automática de comportamientos inseguros con cámaras inteligentes.",
    answer: (
      <>
        Se utilizan{" "}
        <a href="/blog/herramientas-ia-sst" className="text-[#FFD100] font-bold hover:underline">
          algoritmos predictivos
        </a>
        , visión por computadora, asistentes virtuales y detección automática de comportamientos inseguros mediante cámaras inteligentes.
      </>
    ),
  },
  {
    question: "¿La IA puede reducir los accidentes en el trabajo?",
    answerText:
      "Sí. Permite anticipar riesgos antes de que se materialicen, automatizar alertas en zonas peligrosas y crear protocolos personalizados según cada empresa.",
    answer: (
      <>
        Sí. Con IA es posible anticipar riesgos antes de que se materialicen, automatizar alertas en zonas peligrosas y crear{" "}
        <a href="/blog/protocolos-personalizados-ia" className="text-[#FFD100] font-bold hover:underline">
          protocolos personalizados
        </a>{" "}
        según cada empresa.
      </>
    ),
  },
  {
    question: "¿Es costosa la implementación de IA en SST?",
    answerText:
      "No necesariamente. Hay soluciones escalables que se ajustan al tamaño y necesidades de cada empresa, con alto retorno por la reducción de siniestros.",
    answer: (
      <>
        No necesariamente. Existen{" "}
        <a href="/blog/soluciones-escalables-ia" className="text-[#FFD100] font-bold hover:underline">
          soluciones escalables
        </a>{" "}
        que se ajustan al tamaño y necesidades de cada empresa, con retorno de inversión alto por la reducción de siniestros.
      </>
    ),
  },
  {
    question: "¿La IA reemplaza a los profesionales de SST?",
    answerText:
      "No. La IA potencia a los profesionales con mejores herramientas para tomar decisiones más rápidas y efectivas.",
    answer: (
      <>
        No, la IA no reemplaza a los profesionales, los{" "}
        <a href="/blog/profesionales-sst-con-ia" className="text-[#FFD100] font-bold hover:underline">
          potencia con mejores herramientas
        </a>{" "}
        para tomar decisiones más rápidas y efectivas.
      </>
    ),
  },
  {
    question: "¿Qué empresas están aplicando IA en SST actualmente?",
    answerText:
      "Empresas líderes ya implementan IA para monitoreo de riesgos, predicción de accidentes y mejora en procesos preventivos.",
    answer: (
      <>
        Diversas{" "}
        <a href="/blog/empresas-que-usan-ia-sst" className="text-[#FFD100] font-bold hover:underline">
          empresas líderes en seguridad laboral
        </a>{" "}
        ya implementan IA para monitoreo de riesgos, predicción de accidentes y mejora en procesos preventivos.
      </>
    ),
  },
  {
    question: "¿Cómo empezar a implementar IA en una empresa?",
    answerText:
      "Comienza con proyectos piloto en áreas críticas, mide impacto y escala progresivamente con base en resultados.",
    answer: (
      <>
        Se recomienda iniciar con{" "}
        <a href="/blog/proyectos-piloto-ia" className="text-[#FFD100] font-bold hover:underline">
          proyectos piloto
        </a>{" "}
        que permitan medir impacto en áreas críticas, y luego escalar progresivamente con base en los resultados.
      </>
    ),
  },
];

const FaqIA = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const toggle = (i) => setOpenIndex(i === openIndex ? null : i);

  // JSON-LD FAQPage (para rich results)
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map((f) => ({
      "@type": "Question",
      "name": f.question,
      "acceptedAnswer": { "@type": "Answer", "text": f.answerText },
    })),
  };

  return (
    <section
      id="faq-ia-sst"
      className="relative bg-black text-white pt-16 px-6 md:px-12 lg:px-20 pb-0 overflow-hidden"
      aria-label="Preguntas frecuentes sobre IA aplicada en Seguridad y Salud en el Trabajo"
    >
      <style>{`
        .stars, .stars2, .stars3 {
          position: absolute; width: 100%; height: 100%;
          background-repeat: repeat; background-position: center center;
          z-index: 0; pointer-events: none;
        }
        .stars { background-image: radial-gradient(white 1px, transparent 1px); background-size: 3px 3px; opacity: .5; }
        .stars2 { background-image: radial-gradient(white 1.5px, transparent 1.5px); background-size: 5px 5px; opacity: .3; animation: moveStars 100s linear infinite; }
        .stars3 { background-image: radial-gradient(white 2px, transparent 2px); background-size: 7px 7px; opacity: .2; animation: moveStars 180s linear infinite; }
        @keyframes moveStars { from { transform: translateY(0); } to { transform: translateY(-1000px); } }
      `}</style>

      <div className="absolute inset-0 z-0">
        <div className="stars"></div>
      </div>

      <Helmet>
        <title>IASST- IA aplicada en SST | Ernesto Mora</title>
        <meta
          name="description"
          content="Preguntas frecuentes sobre IA aplicada en Seguridad y Salud en el Trabajo: beneficios, herramientas, costos, casos reales y cómo empezar."
        />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="relative z-10 max-w-7xl mx-auto flex flex-col md:flex-row items-stretch gap-10 min-h-[700px]">
        {/* Columna preguntas */}
        <div className="w-full md:w-1/2 flex flex-col justify-start">
          <h2 className="text-5xl md:text-6xl font-black mb-3 leading-tight">
            FAQ — <span className="text-[#FFD100]">IA aplicada en SST</span>
          </h2>
          <p className="text-gray-300 mb-10 max-w-2xl">
            Respuestas claras para líderes HSE sobre <strong>Seguridad y Salud en el Trabajo</strong> con{" "}
            <strong>inteligencia artificial</strong>: prevención de accidentes, inspecciones inteligentes y automatización de informes.
          </p>

          <div className="space-y-6">
            {faqs.map((faq, index) => {
              const isOpen = openIndex === index;
              const panelId = `faq-panel-${index}`;
              const buttonId = `faq-button-${index}`;

              return (
                <article
                  key={index}
                  className="border border-[#FFD100] rounded-lg overflow-hidden transition-all duration-300"
                >
                  <button
                    id={buttonId}
                    onClick={() => toggle(index)}
                    className="w-full flex justify-between items-center px-6 py-4 focus:outline-none transition-all duration-300 group"
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                  >
                    <span
                      className={`text-lg font-semibold transition-all duration-300 ${
                        isOpen ? "text-[#FFD100] text-xl" : "text-white group-hover:text-[#FFD100]"
                      }`}
                    >
                      {faq.question}
                    </span>
                    <motion.span
                      initial={false}
                      animate={{ rotate: isOpen ? 45 : 0 }}
                      transition={{ duration: 0.3 }}
                      className="text-[#FFD100] text-2xl font-bold"
                      aria-hidden="true"
                    >
                      +
                    </motion.span>
                  </button>

                  <AnimatePresence>
                    {isOpen && (
                      <motion.div
                        id={panelId}
                        role="region"
                        aria-labelledby={buttonId}
                        key="content"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.5 }}
                        className="px-6 pb-5 text-gray-300 leading-relaxed"
                      >
                        {faq.answer}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </article>
              );
            })}
          </div>
        </div>

        {/* Columna imagen */}
        <div className="relative w-full md:w-1/2 flex justify-end items-end z-10">
          <img
            src="https://celebratufiesta.com.co/wp-content/uploads/2025/08/IMG_7605.png"
            alt="Ernesto Mora explicando IA aplicada en Seguridad y Salud en el Trabajo"
            className="object-contain w-full h-auto"
            loading="lazy"
            width="800"
            height="900"
          />
        </div>
      </div>
    </section>
  );
};

export default FaqIA;
